import { type Decimal, dec, toFixedTrimmed, ZERO } from "./decimal";
import { type Dimension, type Pack, toCanonical, UNITS, type UnitId } from "./units";

/**
 * Prices are what a brewery paid, so they live in its own database, never in the shared catalog
 * (Tyler, 2026-09-18). A price is stored as the invoice states it, e.g. $32.50 per bag or $0.742 per lb;
 * the cost of a canonical unit is derived from that when needed. Amounts are exact decimals (invariant 4).
 * Shipping, tax and card fees are separate lines, never folded into a unit price.
 */
export const CURRENCY = "USD";

export interface Price {
  amount: string;
  /** What the amount buys: one pack of the lot, or one of a unit. */
  per: UnitId | "pack";
}

export class MoneyError extends Error {}

/** Digits kept for a derived cost per canonical unit. A gram of malt costs about $0.0016. */
export const COST_SCALE = 12;

/** The cost of one canonical unit (a gram, a liter, one each) at this price. */
export function costPerCanonical(price: Price, dimension: Dimension, pack: Pack | null): Decimal {
  const amount = dec(price.amount);
  if (amount.lt(ZERO)) throw new MoneyError("A price cannot be negative");
  if (price.per === "pack" && !pack) {
    throw new MoneyError("This stock has no pack size, so it cannot be priced per pack");
  }
  const canonical = dec(toCanonical({ value: "1", unit: price.per }, dimension, pack));
  return amount.div(canonical);
}

export interface CostedStock {
  /** Canonical quantity of one lot on hand. */
  quantity: string;
  costPerCanonical: string | Decimal | null;
}

export interface CostOnHand {
  /** Cost per canonical unit, weighted by what is left in each priced lot. Null if nothing on hand is priced. */
  average: string | null;
  /** What the priced stock on hand cost. */
  value: string;
  pricedQuantity: string;
  /** Stock on hand with no price: opening counts, borrowed stock, receipts entered without one. */
  unpricedQuantity: string;
}

/**
 * Average cost of what is on hand now. Each lot counts by what is left in it, so the average moves as
 * lots are used up and new ones arrive. Lots at or below zero hold nothing to value.
 */
export function costOnHand(lots: readonly CostedStock[]): CostOnHand {
  let value: Decimal = ZERO;
  let priced: Decimal = ZERO;
  let unpriced: Decimal = ZERO;
  for (const lot of lots) {
    const quantity = dec(lot.quantity);
    if (quantity.lte(ZERO)) continue;
    if (lot.costPerCanonical === null) {
      unpriced = unpriced.plus(quantity);
      continue;
    }
    value = value.plus(quantity.times(dec(lot.costPerCanonical)));
    priced = priced.plus(quantity);
  }
  return {
    average: priced.gt(ZERO) ? toFixedTrimmed(value.div(priced), COST_SCALE) : null,
    value: toFixedTrimmed(value, 6),
    pricedQuantity: toFixedTrimmed(priced, 9),
    unpricedQuantity: toFixedTrimmed(unpriced, 9),
  };
}

/** "$1,234.50". Cents by default; more digits for small unit costs like $0.742/lb. */
export function formatMoney(value: string | Decimal, maxFraction = 2): string {
  const d = dec(value);
  const fixed = toFixedTrimmed(d.abs(), maxFraction);
  const [whole = "0", fraction = ""] = fixed.split(".");
  const cents = fraction.padEnd(2, "0");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${d.lt(ZERO) ? "-" : ""}$${grouped}.${cents}`;
}

/** "$0.742/lb" or "$32.50/bag" from a cost per canonical unit. */
export function formatUnitCost(
  perCanonical: string | Decimal,
  display: { unit: UnitId } | { pack: Pack },
): string {
  if ("pack" in display) {
    return `${formatMoney(dec(perCanonical).times(dec(display.pack.size)))}/${display.pack.name}`;
  }
  const cost = dec(perCanonical).times(dec(UNITS[display.unit].factor));
  const digits = cost.lt(dec("0.1")) ? 4 : cost.lt(dec("10")) ? 3 : 2;
  return `${formatMoney(cost, digits)}/${UNITS[display.unit].label}`;
}
